import { useAppSelector, useAppDispatch } from "../services/hooks";
import React, { useState, useEffect } from 'react';

import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
  Row,
  Col,
  Card,
  CardBody,
  Badge
} from 'reactstrap';
import NotificationAlertOptions from '../layouts/Alerts';
import {
 
  getSites
} from '../services/siteServices/siteActions'
import {
  translatepage,
  translatedpages
} from '../services/translationservice/translationActions'


/** The fields of the source page this modal shows and sends. */
type TranslationPage = {
  _id?: string;
  global_title?: string;
  page_configuration?: { type?: string };
  page_template?: { name?: string };
};

type TranslationModalProps = {
  isOpen: boolean;
  toggle: () => void;
  page?: TranslationPage | null;
  onTranslationComplete?: (responses: unknown[]) => void;
};

const TranslationModal = ({ isOpen, toggle, page, onTranslationComplete }: TranslationModalProps) => {
  const [translating, setTranslating] = useState(false);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<Array<string | undefined>>([]);
  const [error, setError] = useState('');
  // Get sites from Redux like other components
  const allSites = useAppSelector((s) => s.sites.sites || []);
  const sites = allSites;
  const currentSite = useAppSelector((s) => s.sites.site);
  const dispatch = useAppDispatch();

  // Filter out current site and get available sites for translation
  const availableSites = allSites.filter(site => site._id !== currentSite?._id);

  const supportedLanguages: Record<string, string> = {
    nl: 'Dutch',
    pt: 'Portuguese',
    fr: 'French',
    de: 'German',
    es: 'Spanish',
    en: 'English',
    it: 'Italian',
    pl: 'Polish',
    dk: 'Danish'
  };

  useEffect(() => {
    if (sites.length === 0) {
      dispatch(getSites());
    }
    
  }, [sites.length]);

  const handleSiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions);
    const selectedSiteIds = selectedOptions.map(option => option.value);

    setSelectedSites(selectedSiteIds);

    // Get languages for all selected sites
    const languages = selectedSiteIds.map(siteId => {
      const site = availableSites.find(s => String(s._id) === siteId);
      return site?.language_code || '';
    }).filter(lang => lang);

    setSelectedLanguages(languages);
  };
const handleTranslate = async () => {
  if (!selectedSites.length || !selectedLanguages.length) {
    setError('Please select at least one target site');
    return;
  }

  try {
    setTranslating(true);
    setError('');
    if (!page) throw new Error('No source page to translate');

    // Call translation only for the first selected site / language
    const result = await dispatch(
      translatepage({
        pageId: page._id,
        siteId: selectedSites[0],        // 👈 first site
        targetLanguage: selectedLanguages[0], // 👈 first language
      })
    );

    // result is usually the Axios response from the thunk
    const response = result?.data
    const responses = [response]; // keep existing array-based logic

    // Count successful translations
    const successCount =
      responses.filter(r => r && r.success !== false).length;
    const failCount = responses.length - successCount;

    if (successCount > 0) {
      if (failCount === 0) {
        NotificationAlertOptions("success", `Page translated successfully to ${successCount} site(s)!`);
      } else {
        NotificationAlertOptions("warning", `Page translated to ${successCount} site(s), ${failCount} failed.`);
      }

      if (onTranslationComplete) {
        onTranslationComplete(
          responses.filter(r => r && r.success !== false)
        );
      }



      handleClose();
    } else {
      setError('All translations failed. Please try again.');
    }
  } catch (error) {
    console.error('Translation error:', error);
    setError('Translation failed. Please try again.');
  } finally {
    setTranslating(false);
  }
};



  const handleClose = () => {
    setSelectedSites([]);
    setSelectedLanguages([]);
    setError('');
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        <i className="fas fa-language me-2"></i>
        Translate Page to Another Site
      </ModalHeader>

      <ModalBody>
        {page && (
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-2">
                <i className="ni ni-single-copy-04 me-2"></i>
                Source Page
              </h6>
              <p className="mb-1"><strong>Title:</strong> {page.global_title}</p>
              <p className="mb-1"><strong>Type:</strong> {page.page_configuration?.type}</p>
              <p className="mb-0">
                <strong>Template:</strong> {page.page_template?.name || 'No template'}
              </p>
            </CardBody>
          </Card>
        )}

        {error && <Alert color="danger">{error}</Alert>}

        <FormGroup>
          <Label for="targetSite">
            <i className="ni ni-world-2 me-2"></i>
            Select Target Sites
          </Label>
          {availableSites.length === 0 ? (
            <Alert color="info">
              <i className="fas fa-info-circle me-2"></i>
              No other sites available for translation
            </Alert>
          ) : (
            <div className="border rounded p-3" style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {availableSites.map(site => {
                const isSelected = selectedSites.includes(String(site._id));
                return (
                  <div
                    key={String(site._id)}
                    className="d-flex align-items-center p-2 mb-2 border rounded"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#007bff' : '#f8f9fa',
                      color: isSelected ? 'white' : 'inherit',
                      borderColor: isSelected ? '#007bff' : '#dee2e6'
                    }}
                    onClick={() => {
                      const siteId = String(site._id);
                      const language = site.language_code;

                      if (selectedSites.includes(siteId)) {
                        const newSelectedSites = selectedSites.filter(id => id !== siteId);
                        const siteIndex = selectedSites.indexOf(siteId);
                        const newSelectedLanguages = selectedLanguages.filter((_, index) => index !== siteIndex);
                        setSelectedSites(newSelectedSites);
                        setSelectedLanguages(newSelectedLanguages);
                      } else {
                        setSelectedSites([...selectedSites, siteId]);
                        setSelectedLanguages([...selectedLanguages, language]);
                      }
                    }}
                  >
                    <div className="me-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}} // Handled by div onClick
                        style={{ pointerEvents: 'none' }}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <div className="fw-bold">{site.name}</div>
                      <small style={{ opacity: 0.8 }}>
                        {site.language_code?.toUpperCase()} • {site.domain || 'No domain'}
                      </small>
                    </div>
                    <Badge color={isSelected ? 'light' : 'primary'} pill>
                      {site.language_code?.toUpperCase()}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </FormGroup>

        {selectedLanguages.length > 0 && (
          <Alert color="info" className="mt-3">
            <Row className="align-items-center">
              <Col>
                <i className="fas fa-info-circle me-2"></i>
                <strong>Target Languages:</strong>
                <div className="mt-1">
                  {selectedLanguages.map((lang, index) => (
                    <Badge key={selectedSites[index]} color="primary" pill className="me-1">
                      {(lang ? supportedLanguages[lang] : undefined) || lang?.toUpperCase()}
                    </Badge>
                  ))}
                </div>
              </Col>
            </Row>
          </Alert>
        )}

        {selectedSites.length > 0 && selectedLanguages.length > 0 && (
          <Alert color="success" className="mt-3">
            <i className="fas fa-check-circle me-2"></i>
            Ready to translate to {selectedSites.length} site(s) 
          </Alert>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={translating}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleTranslate}
          disabled={selectedSites.length === 0 || selectedLanguages.length === 0 || translating}
        >
          {translating ? (
            <>
              <Spinner size="sm" className="me-2" />
              Translating...
            </>
          ) : (
            <>
              <i className="fas fa-language me-2"></i>
              Translate
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TranslationModal;